import React from "react";
import * as Icon from "react-feather";

import WComponent from "../common/WComponent";
import PlaybookReviewService from "../../services/PlaybookReviewService";
import PlaybookRunService from "../../services/PlaybookRunService";
import UtilityService from "../../services/UtilityService";
import AgentRunDecisionHeader from "./AgentRunDecisionHeader";
import AgentRunRecord from "./AgentRunRecord";
import AgentRunFindings from "./AgentRunFindings";
import AgentRunProposals from "./AgentRunProposals";
import AgentRunOutputs from "./AgentRunOutputs";
import AgentRunSteps from "./AgentRunSteps";
import AgentRunBooksPanel from "./AgentRunBooksPanel";

import "./agent-run-report.css";

const POLL_EVERY_MILLISECONDS = 3000;
const MOST_FAILED_LOADS = 3;

class AgentRunReport extends WComponent {
	constructor(props) {
		super(props);
		this.state = {
			run: null,
			review: null,
			steps: [],
			isLoading: true,
			loadError: null,
			failedLoads: 0,
			actionError: null,
			busySubjectKey: null,
			busyAction: null,
			isActing: false,
			isCancelling: false,
			isResuming: false,
			notResumed: [],
			collapsedAreas: null,
			outputs: [],
			outputsError: null,
			hasLoadedOutputs: false,
			booksPanel: null,
		};
		this.pollTimer = null;
	}

	componentDidMount() {
		document.addEventListener("keydown", this.onKeyDown);
		this.loadRun();
	}

	componentDidUpdate(previousProps) {
		if (previousProps.runId !== this.props.runId) {
			this.stopPolling();
			this.update({ run: null, review: null, steps: [], isLoading: true, loadError: null, failedLoads: 0, outputs: [], outputsError: null, hasLoadedOutputs: false, booksPanel: null });
			this.loadRun();
		}
	}

	componentWillUnmount() {
		document.removeEventListener("keydown", this.onKeyDown);
		this.stopPolling();
	}

	onKeyDown = event => {
		const isEscape = event.key === "Escape";
		if (isEscape && this.state.booksPanel) this.onCloseBooksPanel();
	};

	onOpenBooksPanel = ({ findingId = null, proposalId = null, qboAccountId = null, tab, title }) => {
		this.update({ booksPanel: { findingId, proposalId, qboAccountId, tab, title } });
	};

	onCloseBooksPanel = () => {
		this.update({ booksPanel: null });
	};

	stopPolling = () => {
		if (this.pollTimer) clearTimeout(this.pollTimer);
		this.pollTimer = null;
	};

	loadRun = async () => {
		const { runId } = this.props;
		try {
			const run = await PlaybookRunService.getRun({ runId });
			const lastSequence = this.state.steps.length > 0 ? this.state.steps[this.state.steps.length - 1].sequence : 0;
			const newSteps = await PlaybookRunService.listSteps({ runId, afterSequence: lastSequence });
			const isFinished = PlaybookRunService.isFinished({ status: run.status });
			const hasReview = isFinished && !!run.client_id;
			const review = hasReview ? await PlaybookReviewService.getReview({ runId }) : null;
			const collapsedAreas = this.state.collapsedAreas || (review ? PlaybookReviewService.initiallyCollapsedSections({ review }) : null);
			await this.update({ run, review, collapsedAreas, steps: [...this.state.steps, ...newSteps], isLoading: false, loadError: null, failedLoads: 0 });
			if (isFinished && !this.state.hasLoadedOutputs) this.loadOutputs();
			if (!isFinished) this.pollTimer = setTimeout(this.loadRun, POLL_EVERY_MILLISECONDS);
		} catch (error) {
			const failedLoads = this.state.failedLoads + 1;
			const hasMoreTries = failedLoads < MOST_FAILED_LOADS;
			if (hasMoreTries) {
				this.stopPolling();
				this.pollTimer = setTimeout(this.loadRun, POLL_EVERY_MILLISECONDS);
				await this.update({ failedLoads });
				return;
			}
			await this.update({ failedLoads, isLoading: false, loadError: "This run could not be loaded." });
		}
	};

	loadOutputs = async () => {
		const { runId } = this.props;
		try {
			const outputs = await PlaybookRunService.listRunOutputs({ runId });
			await this.update({ outputs, outputsError: null, hasLoadedOutputs: true });
		} catch (error) {
			const message = UtilityService.describeRequestError({ error, fallback: "The files this run made could not be listed." });
			await this.update({ outputs: [], outputsError: message, hasLoadedOutputs: true });
		}
	};

	actOnReview = async ({ action, subjectKey = null, actionKey = null }) => {
		await this.update({ busySubjectKey: subjectKey, busyAction: actionKey, isActing: true, actionError: null });
		try {
			const review = await action();
			const run = await PlaybookRunService.getRun({ runId: this.props.runId });
			await this.update({ review, run, busySubjectKey: null, busyAction: null, isActing: false });
			return true;
		} catch (error) {
			const message = UtilityService.describeRequestError({ error, fallback: "That did not save. Try again." });
			await this.update({ actionError: message, busySubjectKey: null, busyAction: null, isActing: false });
			return false;
		}
	};

	onDecide = ({ findingId, proposalId, decision, reasonCategory, reason, chosenValue }) => {
		const { runId } = this.props;
		const subjectKey = findingId ? `finding-${findingId}` : `proposal-${proposalId}`;
		return this.actOnReview({
			subjectKey,
			action: () => PlaybookReviewService.decide({ runId, findingId, proposalId, decision, reasonCategory, reason, chosenValue }),
		});
	};

	onExecuteAction = ({ proposalId }) => {
		const { runId } = this.props;
		return this.actOnReview({ subjectKey: `proposal-${proposalId}`, action: () => PlaybookReviewService.executeAction({ runId, proposalId }) });
	};

	onBookJournal = async ({ proposalId, acknowledgedWarnings }) => {
		const { runId } = this.props;
		try {
			await PlaybookReviewService.bookJournal({ runId, proposalId, acknowledgedWarnings });
		} finally {
			const review = await PlaybookReviewService.getReview({ runId });
			const run = await PlaybookRunService.getRun({ runId });
			await this.update({ review, run });
		}
	};

	onRemoveJournal = async ({ proposalId, removeEvenIfChanged }) => {
		const { runId } = this.props;
		try {
			await PlaybookReviewService.removeBookedJournal({ runId, proposalId, removeEvenIfChanged });
		} finally {
			const review = await PlaybookReviewService.getReview({ runId });
			const run = await PlaybookRunService.getRun({ runId });
			await this.update({ review, run });
		}
	};

	onRejectAction = ({ proposalId, reasonCategory, reason }) => {
		return this.onDecide({ proposalId, decision: "reject", reasonCategory, reason, chosenValue: null });
	};

	onConfirmStillApplies = ({ findingId }) => {
		const { runId } = this.props;
		return this.actOnReview({ subjectKey: `finding-${findingId}`, action: () => PlaybookReviewService.confirmStillApplies({ runId, findingId }) });
	};

	onAcceptAllLow = () => {
		const { runId } = this.props;
		return this.actOnReview({ action: () => PlaybookReviewService.acceptAllLow({ runId }) });
	};

	onRecheck = () => {
		const { runId } = this.props;
		return this.actOnReview({ action: () => PlaybookReviewService.recheckBooks({ runId }) });
	};

	onSend = () => {
		const { runId } = this.props;
		return this.actOnReview({ actionKey: "send", action: () => PlaybookReviewService.sendForReview({ runId }) });
	};

	onSignOff = () => {
		const { runId } = this.props;
		return this.actOnReview({ action: () => PlaybookReviewService.signOff({ runId }) });
	};

	onReturn = ({ reason }) => {
		const { runId } = this.props;
		return this.actOnReview({ action: () => PlaybookReviewService.returnToPreparer({ runId, reason }) });
	};

	onCancelRun = async () => {
		const { runId } = this.props;
		await this.update({ isCancelling: true, actionError: null });
		try {
			await PlaybookRunService.cancelRun({ runId });
			await this.update({ isCancelling: false });
			this.stopPolling();
			this.loadRun();
		} catch (error) {
			await this.update({
				isCancelling: false,
				actionError: UtilityService.describeRequestError({ error, fallback: "The run could not be cancelled." }),
			});
		}
	};

	onResumeRun = async () => {
		const { runId } = this.props;
		await this.update({ isResuming: true, actionError: null, notResumed: [] });
		try {
			const resumed = await PlaybookRunService.resumeRun({ runId });
			this.stopPolling();
			await this.update({ isResuming: false, notResumed: resumed.notResumed || [], steps: [] });
			this.loadRun();
		} catch (error) {
			await this.update({
				isResuming: false,
				actionError: UtilityService.describeRequestError({ error, fallback: "The run could not be resumed." }),
			});
		}
	};

	onToggleArea = ({ area, isExpanded }) => {
		this.update({ collapsedAreas: { ...(this.state.collapsedAreas || {}), [area]: isExpanded } });
	};

	renderNotices() {
		const { run, actionError, notResumed } = this.state;
		const { toolChangeNote } = PlaybookRunService.describeRun({ run });
		return (
			<React.Fragment>
				{notResumed.length > 0 && (
					<div className="tk-notice tk-notice--warning">
						<div className="tk-notice__title">
							<Icon.AlertTriangle size={14} />
							Some clients were not resumed
						</div>
						{notResumed.map(reason => (
							<p key={reason} className="tk-notice__detail">
								{reason}
							</p>
						))}
					</div>
				)}
				{toolChangeNote && (
					<div className="tk-notice tk-notice--warning">
						<div className="tk-notice__title">
							<Icon.AlertTriangle size={14} />
							{toolChangeNote.label}
						</div>
						<p className="tk-notice__detail">{toolChangeNote.detail}</p>
					</div>
				)}
				{actionError && (
					<div className="tk-notice tk-notice--danger">
						<div className="tk-notice__title">
							<Icon.AlertTriangle size={14} />
							{actionError}
						</div>
					</div>
				)}
			</React.Fragment>
		);
	}

	renderFinishedRun() {
		const { run, review, steps, outputs, outputsError, collapsedAreas, busySubjectKey, booksPanel } = this.state;
		return (
			<div className="agent-run-report__workspace">
				<div className="agent-run-report__main">
					<AgentRunRecord run={run} review={review}>
						<AgentRunOutputs outputs={outputs} loadError={outputsError} />
						<AgentRunFindings
							review={review}
							collapsedAreas={collapsedAreas}
							busySubjectKey={busySubjectKey}
							openBooksFindingId={booksPanel ? booksPanel.findingId : null}
							onToggleArea={this.onToggleArea}
							onDecide={this.onDecide}
							onConfirmStillApplies={this.onConfirmStillApplies}
							onOpenBooksPanel={this.onOpenBooksPanel}
						/>
						<AgentRunProposals
							run={run}
							review={review}
							collapsedAreas={collapsedAreas}
							busySubjectKey={busySubjectKey}
							openBooksProposalId={booksPanel ? booksPanel.proposalId : null}
							onToggleArea={this.onToggleArea}
							onDecide={this.onDecide}
							onExecuteAction={this.onExecuteAction}
							onRejectAction={this.onRejectAction}
							onBookJournal={this.onBookJournal}
							onRemoveJournal={this.onRemoveJournal}
							onOpenBooksPanel={this.onOpenBooksPanel}
						/>
					</AgentRunRecord>
					<AgentRunSteps steps={steps} run={run} />
				</div>
				{booksPanel && (
					<div className="agent-run-report__books">
						<AgentRunBooksPanel
							key={`${booksPanel.findingId}|${booksPanel.proposalId}|${booksPanel.tab}`}
							runId={run.id}
							findingId={booksPanel.findingId}
							qboAccountId={booksPanel.qboAccountId}
							initialTab={booksPanel.tab}
							title={booksPanel.title}
							onClose={this.onCloseBooksPanel}
						/>
					</div>
				)}
			</div>
		);
	}

	render() {
		const { run, review, steps, isLoading, loadError, isActing, busyAction, isCancelling, isResuming } = this.state;
		const { onBack, backLabel } = this.props;

		if (isLoading) return <div className="tk-empty">Loading run…</div>;
		if (loadError) {
			return (
				<div className="tk-empty">
					{loadError}{" "}
					<button
						type="button"
						className="tk-text-action"
						onClick={async () => {
							await this.update({ failedLoads: 0, loadError: null, isLoading: true });
							this.loadRun();
						}}
					>
						Try again
					</button>
				</div>
			);
		}

		const isFinished = PlaybookRunService.isFinished({ status: run.status });

		return (
			<div className="agent-run-report">
				<AgentRunDecisionHeader
					run={run}
					review={review}
					isActing={isActing}
					busyAction={busyAction}
					isCancelling={isCancelling}
					isResuming={isResuming}
					onBack={onBack}
					backLabel={backLabel}
					onSend={this.onSend}
					onSignOff={this.onSignOff}
					onReturn={this.onReturn}
					onCancelRun={this.onCancelRun}
					onResumeRun={this.onResumeRun}
					onAcceptAllLow={this.onAcceptAllLow}
					onRecheck={this.onRecheck}
				>
					{this.renderNotices()}
				</AgentRunDecisionHeader>
				{!isFinished && <AgentRunSteps steps={steps} run={run} isLive />}
				{isFinished && this.renderFinishedRun()}
			</div>
		);
	}
}

export default AgentRunReport;
